import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysCompanyAccount } from '../queryKeys';

import { createCompanyAccount } from './createCompanyAccount';
import {
  type CreateCompanyAccountPayload,
  type CreatedCompanyAccount,
} from './types';

/**
 * Custom hook for creating a company account using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company-Account/operation/create_company_acc_company_account_post
 */
export const useMutationCreateCompanyAccount = (
  options?: Omit<
    UseMutationOptions<
      CreatedCompanyAccount,
      Error,
      CreateCompanyAccountPayload
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<CreatedCompanyAccount, Error, CreateCompanyAccountPayload>(
    {
      mutationFn: createCompanyAccount,
      ...options,
      onSettled: (data, error, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({
          queryKey: queryKeysCompanyAccount.getAllAdmin({}),
        });

        options?.onSettled?.(data, error, variables, onMutateResult, context);
      },
    },
  );
};
